import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';
import PageHeader, { btnPrimary, btnSecondary, inputClass, cardClass } from '../components/PageHeader';
import SearchableSelect from '../components/SearchableSelect';

const empty = {
  account_id: '',
  label: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  access_notes: '',
  is_active: true,
};

const LocationForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/admin/accounts?include_inactive=true').then((r) => setAccounts(r.data || []));
  }, []);

  useEffect(() => {
    if (!isEdit) {
      setLoading(false);
      return;
    }
    apiFetch(`/admin/locations/${id}`)
      .then((r) => {
        const l = r.data;
        setForm({
          account_id: l.account_id,
          label: l.label || '',
          address_line1: l.address_line1,
          address_line2: l.address_line2 || '',
          city: l.city,
          state: l.state,
          postal_code: l.postal_code,
          access_notes: l.access_notes || '',
          is_active: l.is_active,
        });
      })
      .catch(() => toast.error('Failed to load location'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        const { account_id, ...body } = form;
        await apiFetch(`/admin/locations/${id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        toast.success('Location updated');
      } else {
        await apiFetch('/admin/locations', {
          method: 'POST',
          body: JSON.stringify(form),
        });
        toast.success('Location created');
      }
      navigate('/locations');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    }
  };

  if (loading) return <p className="text-slate-500">Loading…</p>;

  return (
    <div className="max-w-xl">
      <PageHeader title={isEdit ? 'Edit location' : 'New location'} />
      <Link to="/locations" className="text-sm text-teal-700 mb-4 inline-block">
        ← Locations
      </Link>
      <form onSubmit={submit} className={`${cardClass} p-6 space-y-4`}>
        {!isEdit && (
          <SearchableSelect
            label="Account *"
            value={form.account_id}
            onChange={(account_id) => setForm({ ...form, account_id })}
            options={accounts
              .filter((a) => a.is_active)
              .map((a) => ({ value: a.id, label: a.name }))}
            placeholder="Search accounts…"
            required
          />
        )}
        {isEdit && (
          <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
            Account cannot be changed after creation. Create a new location to move sites.
          </p>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">Label</label>
          <input
            className={inputClass}
            placeholder="e.g. Building A, Home"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Address line 1 *</label>
          <input
            className={inputClass}
            value={form.address_line1}
            onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Address line 2</label>
          <input
            className={inputClass}
            value={form.address_line2}
            onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">City *</label>
            <input
              className={inputClass}
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">State *</label>
            <input
              className={inputClass}
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Postal code *</label>
          <input
            className={inputClass}
            value={form.postal_code}
            onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Access notes</label>
          <textarea
            className={inputClass}
            rows={3}
            value={form.access_notes}
            onChange={(e) => setForm({ ...form, access_notes: e.target.value })}
            placeholder="Gate code, parking, etc."
          />
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        )}
        <div className="flex gap-3 pt-2">
          <button type="submit" className={btnPrimary}>
            Save
          </button>
          <Link to="/locations" className={btnSecondary}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
};

export default LocationForm;
